import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Flag, CheckCircle, XCircle, Clock, Trash2, ShieldOff, BellOff, ChevronDown, ChevronUp, Image as ImageIcon, MessageSquare, User } from "lucide-react";

interface Report {
  id: number;
  reporterId: number | null;
  reporterName: string | null;
  reporterEmail: string | null;
  targetType: string;
  targetId: number;
  reason: string;
  notes: string | null;
  status: "pending" | "reviewed" | "dismissed";
  createdAt: string;
  content: string | null;
  imageUrl: string | null;
  postId: number | null;
  posterUserId: number | null;
  posterName: string | null;
  targetUserName: string | null;
}

interface ThreadMessage {
  id: number;
  text: string;
  senderId: number;
  senderName: string | null;
  createdAt: string;
  conversationId: number;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  offensive: "Offensive content",
  misinformation: "Misinformation",
  harassment: "Harassment",
  inappropriate_image: "Inappropriate image",
  scam: "Scam",
  other: "Other",
};

const TARGET_LABELS: Record<string, string> = {
  community_post: "Community Post",
  community_comment: "Comment",
  deal: "Deal",
  event: "Event",
  news: "News Article",
  business: "Business",
  review: "Review",
  user: "User",
  free_item: "Free Item",
};

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: Clock, color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200" },
  reviewed: { label: "Reviewed", icon: CheckCircle, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  dismissed: { label: "Dismissed", icon: XCircle, color: "text-slate-500 bg-slate-50 border-slate-200" },
};

const BAN_DURATIONS = [1, 3, 7, 14, 30];

type ActionPanel = "remove" | "ban" | "noaction" | null;

function ReportCard({ report, onActionDone }: { report: Report; onActionDone: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [actionPanel, setActionPanel] = useState<ActionPanel>(null);
  const [banDays, setBanDays] = useState(7);
  const [customMsg, setCustomMsg] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadFound, setThreadFound] = useState<boolean | null>(null);

  const cfg = STATUS_CONFIG[report.status];
  const StatusIcon = cfg.icon;
  const hasContent = report.content || report.imageUrl;
  const isCommunityContent = report.targetType === "community_post" || report.targetType === "community_comment";
  const isUserReport = report.targetType === "user";

  const defaultMessages = {
    remove: `Your ${report.targetType === "community_post" ? "post" : "comment"} has been removed by a moderator as it was found to violate our community guidelines.`,
    ban: `Your ${report.targetType === "community_post" ? "post" : "comment"} has been removed and you have been banned from the community board for ${banDays} day${banDays === 1 ? "" : "s"} due to a violation of our community guidelines.`,
    noaction: "Thank you for your report. After reviewing the content, we have decided no further action is needed at this time.",
  };

  const openPanel = (panel: ActionPanel) => {
    setActionPanel(prev => prev === panel ? null : panel);
    setCustomMsg("");
  };

  const doAction = async (endpoint: string, body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/reports/${report.id}/${endpoint}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast({ title: endpoint === "no-action" ? "Reporter notified, report dismissed" : "Action taken successfully" });
      onActionDone();
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const toggleThread = async () => {
    if (showThread) { setShowThread(false); return; }
    if (threadFound !== null) { setShowThread(true); return; }
    if (!report.reporterId || !report.targetId) return;
    setThreadLoading(true);
    setShowThread(true);
    try {
      const res = await fetch(`/api/admin/conversations/between/${report.reporterId}/${report.targetId}`, {
        headers: authHeaders(),
      });
      const data = await res.json() as { messages: ThreadMessage[]; found: boolean };
      setThreadMessages(data.messages ?? []);
      setThreadFound(data.found ?? false);
    } catch {
      setThreadFound(false);
    } finally {
      setThreadLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isUserReport ? "bg-blue-100" : "bg-red-100"}`}>
            {isUserReport ? <User size={16} className="text-blue-500" /> : <Flag size={16} className="text-red-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="font-semibold text-sm text-slate-800">
                  {isUserReport
                    ? <>Reported User: <span className="text-blue-700">{report.targetUserName ?? `#${report.targetId}`}</span></>
                    : <>{TARGET_LABELS[report.targetType] ?? report.targetType} #{report.targetId}
                        {report.posterName && <span className="font-normal text-slate-500"> · by {report.posterName}</span>}
                      </>
                  }
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Reported by {report.reporterName ?? "Unknown"}{report.reporterEmail ? ` (${report.reporterEmail})` : ""}
                  {" · "}{new Date(report.createdAt).toLocaleDateString("en-GB")}
                </p>
              </div>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                <StatusIcon size={11} />
                {cfg.label}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs bg-red-50 text-red-700 border border-red-100 px-2 py-0.5 rounded-full font-medium">
                {REASON_LABELS[report.reason] ?? report.reason}
              </span>
              {report.notes && (
                <span className="text-xs text-slate-500 italic">"{report.notes}"</span>
              )}
            </div>
          </div>
        </div>

        {/* Content preview (community posts, free items, deals, events) */}
        {hasContent && (
          <div className="mt-3 ml-12 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden">
            {report.imageUrl && (
              <div className="border-b border-slate-100">
                {showImage ? (
                  <img
                    src={report.imageUrl}
                    alt="Reported content"
                    className="w-full max-h-64 object-cover cursor-pointer"
                    onClick={() => setShowImage(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowImage(true)}
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs text-slate-500 hover:bg-slate-100 transition-colors"
                  >
                    <ImageIcon size={13} />
                    <span>Show image</span>
                  </button>
                )}
              </div>
            )}
            {report.content && (
              <p className="px-3 py-2.5 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{report.content}</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        {report.status === "pending" && isCommunityContent && (
          <div className="flex flex-wrap gap-2 mt-3 ml-12">
            <button
              onClick={() => openPanel("remove")}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                actionPanel === "remove"
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-red-600 border-red-200 hover:bg-red-50"
              }`}
            >
              <Trash2 size={12} />
              Remove {report.targetType === "community_post" ? "Post" : "Comment"}
              {actionPanel === "remove" ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            <button
              onClick={() => openPanel("ban")}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                actionPanel === "ban"
                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-white text-orange-600 border-orange-200 hover:bg-orange-50"
              }`}
            >
              <ShieldOff size={12} />
              Remove & Ban
              {actionPanel === "ban" ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            <button
              onClick={() => openPanel("noaction")}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                actionPanel === "noaction"
                  ? "bg-slate-600 text-white border-slate-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <BellOff size={12} />
              No Action
              {actionPanel === "noaction" ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        )}

        {/* User report actions + thread button */}
        {report.status === "pending" && isUserReport && (
          <div className="flex flex-wrap gap-2 mt-3 ml-12">
            <button
              onClick={() => void toggleThread()}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                showThread
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-blue-900/20"
              }`}
            >
              <MessageSquare size={12} />
              View Message Thread
              {showThread ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fetch(`/api/admin/reports/${report.id}`, {
                    method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "reviewed" }),
                  });
                  if (!res.ok) throw new Error();
                  toast({ title: "Marked as reviewed" });
                  onActionDone();
                } catch {
                  toast({ title: "Failed", variant: "destructive" });
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <CheckCircle size={12} /> Mark Reviewed
            </button>
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fetch(`/api/admin/reports/${report.id}`, {
                    method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "dismissed" }),
                  });
                  if (!res.ok) throw new Error();
                  toast({ title: "Report dismissed" });
                  onActionDone();
                } catch {
                  toast({ title: "Failed", variant: "destructive" });
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="flex items-center gap-1 text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <XCircle size={12} /> Dismiss
            </button>
          </div>
        )}

        {/* Also show "View Thread" for already-resolved user reports */}
        {report.status !== "pending" && isUserReport && report.reporterId && (
          <div className="mt-3 ml-12">
            <button
              onClick={() => void toggleThread()}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                showThread
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-blue-900/20"
              }`}
            >
              <MessageSquare size={12} />
              View Message Thread
              {showThread ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        )}

        {/* Non-user, non-community legacy actions */}
        {report.status === "pending" && !isCommunityContent && !isUserReport && (
          <div className="flex gap-2 mt-3 ml-12">
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fetch(`/api/admin/reports/${report.id}`, {
                    method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "reviewed" }),
                  });
                  if (!res.ok) throw new Error();
                  toast({ title: "Marked as reviewed" });
                  onActionDone();
                } catch {
                  toast({ title: "Failed", variant: "destructive" });
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <CheckCircle size={12} /> Mark Reviewed
            </button>
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fetch(`/api/admin/reports/${report.id}`, {
                    method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "dismissed" }),
                  });
                  if (!res.ok) throw new Error();
                  toast({ title: "Report dismissed" });
                  onActionDone();
                } catch {
                  toast({ title: "Failed", variant: "destructive" });
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="flex items-center gap-1 text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <XCircle size={12} /> Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Conversation thread panel */}
      {showThread && (
        <div className="border-t border-blue-100 bg-blue-50 dark:bg-blue-900/20 p-4">
          <p className="text-xs font-semibold text-blue-700 mb-3 flex items-center gap-1.5">
            <MessageSquare size={13} />
            Message thread between {report.reporterName ?? "reporter"} and {report.targetUserName ?? "reported user"}
          </p>
          {threadLoading ? (
            <div className="text-xs text-blue-500 animate-pulse">Loading thread…</div>
          ) : threadFound === false || threadMessages.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No direct message thread found between these users.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {threadMessages.map(msg => {
                const isReporter = msg.senderId === report.reporterId;
                return (
                  <div key={msg.id} className={`flex gap-2 ${isReporter ? "" : "flex-row-reverse"}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${isReporter ? "bg-slate-200 text-slate-600" : "bg-blue-200 text-blue-700"}`}>
                      {(msg.senderName ?? "?").charAt(0).toUpperCase()}
                    </div>
                    <div className={`max-w-[75%] rounded-xl px-3 py-2 ${isReporter ? "bg-white border border-slate-200" : "bg-blue-600 text-white"}`}>
                      <p className={`text-xs font-semibold mb-0.5 ${isReporter ? "text-slate-500" : "text-blue-100"}`}>{msg.senderName ?? "Unknown"}</p>
                      <p className={`text-xs leading-relaxed ${isReporter ? "text-slate-700" : "text-white"}`}>{msg.text}</p>
                      <p className={`text-xs mt-1 ${isReporter ? "text-slate-400" : "text-blue-200"}`}>
                        {new Date(msg.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Action panels */}
      {actionPanel === "remove" && (
        <div className="border-t border-red-100 bg-red-50 p-4">
          <p className="text-xs font-semibold text-red-700 mb-2">Remove content — message sent to poster:</p>
          <textarea
            className="w-full text-xs border border-red-200 rounded-lg px-3 py-2 bg-white resize-none outline-none focus:border-red-400"
            rows={3}
            value={customMsg || defaultMessages.remove}
            onChange={e => setCustomMsg(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => void doAction("remove-content", { customMessage: customMsg || defaultMessages.remove })}
              disabled={busy}
              className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {busy ? "Removing…" : "Confirm Remove"}
            </button>
            <button onClick={() => setActionPanel(null)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}

      {actionPanel === "ban" && (
        <div className="border-t border-orange-100 bg-orange-50 p-4">
          <p className="text-xs font-semibold text-orange-700 mb-2">Remove content + ban poster from community:</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {BAN_DURATIONS.map(d => (
              <button
                key={d}
                onClick={() => setBanDays(d)}
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                  banDays === d
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-orange-700 border-orange-200 hover:bg-orange-100"
                }`}
              >
                {d} day{d === 1 ? "" : "s"}
              </button>
            ))}
          </div>
          <p className="text-xs font-semibold text-orange-700 mb-1">Message sent to poster:</p>
          <textarea
            className="w-full text-xs border border-orange-200 rounded-lg px-3 py-2 bg-white resize-none outline-none focus:border-orange-400"
            rows={3}
            value={customMsg || `Your ${report.targetType === "community_post" ? "post" : "comment"} has been removed and you have been banned from the community board for ${banDays} day${banDays === 1 ? "" : "s"} due to a violation of our community guidelines.`}
            onChange={e => setCustomMsg(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => void doAction("remove-and-ban", {
                days: banDays,
                customMessage: customMsg || `Your ${report.targetType === "community_post" ? "post" : "comment"} has been removed and you have been banned from the community board for ${banDays} day${banDays === 1 ? "" : "s"} due to a violation of our community guidelines.`,
              })}
              disabled={busy}
              className="text-xs font-semibold bg-orange-600 hover:bg-orange-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {busy ? "Applying…" : `Remove & Ban ${banDays}d`}
            </button>
            <button onClick={() => setActionPanel(null)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}

      {actionPanel === "noaction" && (
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          <p className="text-xs font-semibold text-slate-700 mb-2">Message sent to reporter:</p>
          <textarea
            className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white resize-none outline-none focus:border-slate-400"
            rows={3}
            value={customMsg || defaultMessages.noaction}
            onChange={e => setCustomMsg(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => void doAction("no-action", { customMessage: customMsg || defaultMessages.noaction })}
              disabled={busy}
              className="text-xs font-semibold bg-slate-600 hover:bg-slate-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : "Notify & Dismiss"}
            </button>
            <button onClick={() => setActionPanel(null)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminReports() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "reviewed" | "dismissed">("pending");

  if (authLoading) return null;
  if (user?.role !== "admin" && !(user as { isModerator?: boolean } | null)?.isModerator) return <Redirect to="/" />;

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/reports?limit=100", { headers: authHeaders() });
      const data = await res.json() as { items: Report[] };
      setReports(data.items ?? []);
    } catch {
      toast({ title: "Failed to load reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void fetchReports(); }, [fetchReports]);

  const filtered = statusFilter === "all" ? reports : reports.filter(r => r.status === statusFilter);
  const pendingCount = reports.filter(r => r.status === "pending").length;

  return (
    <AdminLayout current="/admin/reports">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Reports</h1>
            <p className="text-sm text-slate-500 mt-0.5">User-reported content that needs review</p>
          </div>
          {pendingCount > 0 && (
            <span className="text-sm font-semibold px-3 py-1 rounded-full bg-red-100 text-red-700">
              {pendingCount} pending
            </span>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {(["pending", "reviewed", "dismissed", "all"] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                statusFilter === s
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
              }`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              {s === "pending" && pendingCount > 0 && ` (${pendingCount})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-12 text-center">
            <Flag size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No {statusFilter !== "all" ? statusFilter : ""} reports</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(report => (
              <ReportCard key={report.id} report={report} onActionDone={fetchReports} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
